// Seite, zu der die KI-Hilfe gefragt wird: Schlüssel (wie der Seitenkatalog im Backend, internal/modules/assistant/
// pages.go) und sichtbare Bezeichnung, abgeleitet aus Pfad und Reiter (?ansicht= bzw. ?tab=).

export interface PageContext {
  key: string;
  label: string;
}

const areas: Record<string, string> = {
  '': 'Heute',
  akquise: 'Akquise',
  posteingang: 'Posteingang',
  leads: 'Leads',
  termine: 'Termine',
  gebiete: 'Gebiete & Makler',
  gespraeche: 'Gespräche',
  vertrieb: 'Vertrieb',
  verwaltung: 'Verwaltung',
  profil: 'Mein Profil',
  tickets: 'Rückfragen',
  makler: 'Makler',
  expose: 'Exposé',
  dokumente: 'Dokument',
  'ki-pruefung': 'KI-Prüfung',
  geo: 'Geo-Klärung',
};

const views: Record<string, string> = {
  rueckfragen: 'Rückfragen',
  wiedervorlagen: 'Rückrufe & Wiedervorlagen',
  'ohne-makler': 'Termine ohne Makler',
  adressen: 'Adresse unklar',
  ki: 'KI-Prüfung',
  outlook: 'Outlook-Fehler',
  liste: 'Liste',
  karte: 'Karte',
  regionen: 'Regionen',
  meine: 'Meine Termine',
  kalender: 'Kalender',
  bestaetigungen: 'Terminbestätigungen',
  uebersicht: 'Übersicht',
  verwalten: 'Gebiete verwalten',
  projekt: 'Projekt',
  projekte: 'Alle Projekte',
  vorlagen: 'Dokumentvorlagen',
  integrationen: 'Integrationen & System',
  email: 'E-Mail',
  benutzer: 'Benutzer',
  hinweis: 'Hinweis beim Login',
  jobs: 'Fehlgeschlagene Jobs',
  datenschutz: 'Datenschutz',
};

// Standardreiter je Bereich (ohne ?ansicht= öffnet der Bereich diesen Reiter).
const defaultViews: Record<string, string> = { leads: 'liste', gebiete: 'uebersicht' };

const details: Record<string, string> = {
  leads: 'Lead-Detailseite',
  termine: 'Termin-Detailseite',
  gebiete: 'Gebiet (Details)',
  tickets: 'Rückfrage (Details)',
  makler: 'Makler (Details)',
  expose: 'Exposé bearbeiten',
  dokumente: 'Dokument bearbeiten',
};

export function pageContext(pathname: string, search: string): PageContext {
  const rest = pathname.replace(/^\/p\/[^/]+\/?/, '');
  const [area = '', id, sub] = rest.split('/');
  const areaLabel = areas[area] ?? 'Maklerpass';
  if (area === 'akquise' && id) return { key: 'akquise', label: 'Akquise' };
  if (area === 'leads' && id && sub === 'unterlagen') return { key: 'leads/unterlagen', label: 'Unterlagen anfordern' };
  if (id && details[area]) return { key: `${area}/detail`, label: details[area] };
  if (area === 'geo' && id === 'klaerung') return { key: 'posteingang/adressen', label: 'Geo-Klärung (Adresse unklar)' };
  const params = new URLSearchParams(search);
  const view = params.get('ansicht') ?? params.get('tab') ?? defaultViews[area];
  if (area === 'heute' || area === '') return { key: 'heute', label: 'Heute' };
  if (view && views[view]) return { key: `${area}/${view}`, label: `${areaLabel} – ${views[view]}` };
  return { key: area, label: areaLabel };
}

const suggestions: Record<string, string[]> = {
  heute: ['Was sehe ich auf der Startseite?', 'Wie komme ich zum nächsten Anruf?'],
  akquise: ['Wie starte ich den nächsten Anruf?', 'Wie buche ich einen Termin?', 'Wie bestätige ich einen Termin telefonisch?'],
  'akquise/wiedervorlagen': ['Wie erledige ich eine Wiedervorlage?', 'Wie verschiebe ich einen Rückruf?'],
  'posteingang/adressen': ['Wie kläre ich eine unklare Adresse?', 'Wie übernehme ich Google-Vorschläge gesammelt?', 'Was heißt „Wahrscheinlich zugeordnet“?'],
  'posteingang/rueckfragen': ['Wie bearbeite ich eine Rückfrage?', 'Was ist eine Terminrückgabe?'],
  'posteingang/ohne-makler': ['Wie weise ich einem Termin einen Makler zu?'],
  'leads/liste': ['Wie finde ich einen bestimmten Lead?', 'Welche Filter gibt es?'],
  'leads/detail': ['Was sehe ich auf der Lead-Seite?', 'Wie fordere ich Unterlagen an?'],
  'termine/kalender': ['Wie filtere ich den Kalender nach Makler?', 'Wie verschiebe ich einen Termin?'],
  'termine/bestaetigungen': ['Wann ist eine Terminbestätigung fällig?', 'Was trage ich bei „Nicht erreicht“ ein?'],
  'termine/detail': ['Wie verschiebe oder sage ich einen Termin ab?', 'Was steht in der Terminvorbereitung?'],
  'gebiete/uebersicht': ['Wie ordne ich einem Gebiet einen Makler zu?'],
  'gebiete/verwalten': ['Wie lege ich ein neues Gebiet an?', 'Wie ordne ich PLZ oder Landkreise zu?'],
  'verwaltung/projekt': ['Wo stelle ich die Anrufzeiten ein?', 'Wie füge ich ein Projektmitglied hinzu?', 'Wie ändere ich den Gesprächsleitfaden?'],
  'verwaltung/benutzer': ['Wie lege ich einen Benutzer an?', 'Wie setze ich ein Passwort zurück?'],
  profil: ['Wie ändere ich mein Passwort?', 'Wie stelle ich Benachrichtigungen ein?'],
};

export function suggestionsFor(key: string): string[] {
  return suggestions[key] ?? ['Was kann ich auf dieser Seite machen?', 'Wo finde ich …?'];
}
